import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { usePairAgent, useAgentStatus, useAgentVersion } from "../api/agent";
import { ApiError } from "../api/client";
import type { AgentInfo } from "../api/types";
import { messageFor } from "../copy/errors";
import { formatRelative } from "../ui/format";
import { useNow } from "../ui/useNow";
import {
  Button,
  Card,
  DataTable,
  Field,
  Input,
  PageHeader,
  Pill,
  Skeleton,
} from "../ui";

/** The code the agent prints is `XXXX-XXXX` (RFC 8628 user code); the API
 *  accepts 8–12 characters and normalises case and the hyphen itself. */
export const USER_CODE_MIN = 8;
export const USER_CODE_MAX = 12;
export const MACHINE_NAME_MAX = 100;

/** The first eight characters of a device id, the way the agent itself logs
 *  it; a worker without one (never registered) shows a dash. */
export function shortDeviceId(deviceId: string | null): string {
  if (!deviceId) return "—";
  return deviceId.length > 8 ? deviceId.slice(0, 8) : deviceId;
}

/** Copy for a failed pairing: the Accounts device-grant codes are not in
 *  docs/07 §3, so they are spelled out here; anything else takes the standard
 *  sentence. `forbidden` has two causes on this endpoint (docs/03 §6) and the
 *  sentence names both. */
export function pairErrorCopy(error: unknown): string {
  if (!(error instanceof ApiError)) return messageFor(undefined);
  switch (error.code) {
    case "code_not_found":
      return "That code was not recognised. Check it against what the agent printed and try again.";
    case "code_expired":
      return "That code has expired. Run `truereel-agent pair` again for a fresh one.";
    case "forbidden":
      return "You cannot pair a device right now: sign out and back in, or check that bring-your-own GPU is enabled for your organisation.";
    default:
      return messageFor(error.code);
  }
}

const INSTALL = ["pip install truereel-agent", "truereel-agent pair"];

function AgentRow({ agent, now }: { agent: AgentInfo; now: number }) {
  return (
    <tr data-testid={`agent-${agent.device_id ?? "none"}`}>
      <td className="font-semibold text-slate-800">
        <code title={agent.device_id ?? undefined}>
          {shortDeviceId(agent.device_id)}
        </code>
      </td>
      <td>{agent.gpu_class ?? "—"}</td>
      <td>{agent.blender_version ?? "—"}</td>
      <td>{agent.os ?? "—"}</td>
      <td>
        <Pill size="sm" tone={agent.online ? "success" : "neutral"}>
          {agent.online ? "online" : "offline"}
        </Pill>
      </td>
      <td className="text-slate-600">
        {agent.last_seen_at ? (
          <time dateTime={agent.last_seen_at} title={agent.last_seen_at}>
            {formatRelative(agent.last_seen_at, now)}
          </time>
        ) : (
          "never"
        )}
      </td>
    </tr>
  );
}

function PairForm() {
  const pair = usePairAgent();
  // `truereel-agent pair` prints /agent/pair?code=BCDF-2345; the code arrives pre-filled.
  const [params] = useSearchParams();
  const [code, setCode] = useState(params.get("code") ?? "");
  const [name, setName] = useState("");
  const [paired, setPaired] = useState<string | null>(null);
  const trimmedCode = code.trim();
  const trimmedName = name.trim();
  const codeOk =
    trimmedCode.length >= USER_CODE_MIN && trimmedCode.length <= USER_CODE_MAX;
  const canPair =
    codeOk &&
    trimmedName.length > 0 &&
    trimmedName.length <= MACHINE_NAME_MAX &&
    !pair.isPending;

  const submit = () => {
    if (!canPair) return;
    setPaired(null);
    pair.mutate(
      { user_code: trimmedCode, name: trimmedName },
      {
        onSuccess: (result) => {
          setPaired(result.name);
          setCode("");
          setName("");
        },
      },
    );
  };

  return (
    <form
      className="flex flex-col gap-4"
      aria-label="Pair a machine"
      onSubmit={(e) => {
        e.preventDefault();
        submit();
      }}
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <Field
          label="Pairing code"
          helper="The code the agent printed, like BCDF-2345."
        >
          <Input
            value={code}
            autoComplete="off"
            autoCapitalize="characters"
            spellCheck={false}
            maxLength={USER_CODE_MAX}
            placeholder="BCDF-2345"
            className="font-mono uppercase"
            onChange={(e) => setCode(e.target.value)}
          />
        </Field>
        <Field label="Machine name" helper="So you can tell your GPUs apart.">
          <Input
            value={name}
            maxLength={MACHINE_NAME_MAX}
            placeholder="Studio 3090"
            onChange={(e) => setName(e.target.value)}
          />
        </Field>
      </div>
      {pair.isError ? (
        <p role="alert" className="m-0 text-sm text-slate-700">
          {pairErrorCopy(pair.error)}
        </p>
      ) : null}
      {paired ? (
        <p role="status" className="m-0 text-sm text-slate-700">
          Paired {paired}. It appears below as soon as it checks in.
        </p>
      ) : null}
      <div>
        <Button
          type="submit"
          variant="primary"
          disabled={!canPair}
          aria-busy={pair.isPending}
        >
          {pair.isPending ? "Pairing…" : "Pair"}
        </Button>
      </div>
    </form>
  );
}

/** `/agent` (docs/03 §6, docs/08 §1): install and pair the TrueReel Agent,
 *  and see the org's machines with their last heartbeat. Status is re-read
 *  every 30 s while the page is open. */
export function AgentPage() {
  const status = useAgentStatus();
  const version = useAgentVersion();
  const now = useNow(60_000);

  return (
    <>
      <PageHeader eyebrow="— Your GPU" title="Render on your own GPU" />
      {status.isPending ? (
        <Card aria-busy="true" className="flex flex-col gap-3 p-6">
          <Skeleton height={14} width="60%" />
          <Skeleton height={14} width="40%" />
          <Skeleton height={64} />
        </Card>
      ) : status.isError ? (
        <Card role="alert" className="p-6 text-slate-700">
          {messageFor(
            status.error instanceof ApiError ? status.error.code : undefined,
          )}
        </Card>
      ) : !status.data.byo_enabled ? (
        <Card className="flex flex-col gap-2 p-6" data-testid="byo-disabled">
          <p className="m-0 text-lg font-semibold text-slate-800">
            Bring-your-own GPU is not enabled for this organisation.
          </p>
          <p className="m-0 text-slate-600">
            Your jobs render in the cloud. Ask us to switch it on if you have a
            GPU you would rather use.
          </p>
        </Card>
      ) : (
        <div className="flex flex-col gap-6">
          <Card className="flex flex-col gap-4 p-6">
            <p className="m-0 text-slate-700">
              The TrueReel Agent is a small program that renders your
              organisation&apos;s jobs on your own GPU, at the bring-your-own
              rate: you pay for orchestration and storage, not for GPU time. It
              only ever renders your own jobs, and only the ones you mark
              &ldquo;Render on my GPU&rdquo;.
            </p>
            <div>
              <div className="tb-field-label mb-2">Install and pair</div>
              <pre
                className="m-0 overflow-x-auto rounded bg-slate-900 p-4 text-sm text-slate-100"
                data-testid="install"
              >
                <code>{INSTALL.join("\n")}</code>
              </pre>
              <p className="mb-0 mt-2 text-sm text-slate-500">
                The second command prints a code. Enter it here with a name for
                the machine.
              </p>
            </div>
            <PairForm />
          </Card>

          {status.data.agents.length === 0 ? (
            <Card className="p-6 text-slate-600" data-testid="no-agents">
              No machines are paired yet. Once one is, it appears here with its
              GPU and when it last checked in.
            </Card>
          ) : (
            <Card className="overflow-x-auto p-0">
              <DataTable density="compact" aria-label="Your machines">
                <thead>
                  <tr>
                    <th>Machine</th>
                    <th>GPU</th>
                    <th>Blender</th>
                    <th>OS</th>
                    <th>Status</th>
                    <th>Last seen</th>
                  </tr>
                </thead>
                <tbody>
                  {status.data.agents.map((agent, i) => (
                    <AgentRow
                      key={agent.device_id ?? `agent-${i}`}
                      agent={agent}
                      now={now}
                    />
                  ))}
                </tbody>
              </DataTable>
            </Card>
          )}

          <p className="m-0 text-xs text-slate-500" data-testid="agent-version">
            Current agent version: {status.data.version}.
            {version.data ? (
              <>
                {" "}
                <a href={version.data.url} target="_blank" rel="noreferrer">
                  Releases
                </a>
              </>
            ) : null}
          </p>
        </div>
      )}
    </>
  );
}
